"use strict";

var PRODUCTION_MODE = {
  STANDARD: "standard_mode",
  ECONOMY: "economy_mode",
  FAST: "fast_mode",
  NEXT_DAY: "next_day_mode",
};

/**
 * Functions to help extract prices, delays, errors from a "priceData" structure given a production_mode
 * This is needed mainly because standard_mode vs the other modes are using a slightly different way of storing their infos
 *
 * Price:
 *  - in standard_mode the pricing information is in priceData.price + finish and/or painting delta found in priceData.options.<mode>.<finish/painting>
 *  - in all but standard_mode the pricing information is in priceData.options[<mode>] and already accounts for finish and painting price
 *
 * Errors:
 *  - in standard_mode the error is in .error and .error_info (new format c.f comment below)
 *  - in all but standard_mode the errors are in .options[<mode>].errors (but no new format it seems)
 *
 * Delivery:
 *  -
 * TODO: example priceData, tests
 */
var PriceDataUtils = {
  isValidMode: function (productionMode) {
    return (
      [
        PRODUCTION_MODE.STANDARD,
        PRODUCTION_MODE.ECONOMY,
        PRODUCTION_MODE.FAST,
        PRODUCTION_MODE.NEXT_DAY,
      ].indexOf(productionMode) !== -1
    );
  },

  getTotalPrice: function (
    priceData,
    productionMode,
    finish,
    painting,
    business_rewards
  ) {
    if (!this.isValidMode(productionMode)) {
      console.error("Invalid production mode '" + productionMode + "'");
      return null;
    }

    var price_key = "total_cost";
    if (business_rewards) {
      price_key = "total_cost_no_business_rewards";
    }
    if (productionMode === PRODUCTION_MODE.STANDARD) {
      if (priceData.error) return null;

      return (
        priceData.price[price_key] +
        priceData.quantity *
          PriceDataUtils._getFinishAndPaintingPrice(priceData, finish, painting)
      );
    } else if (
      priceData.hasOwnProperty("options") &&
      priceData.options.hasOwnProperty(productionMode)
    ) {
      if (priceData.options[productionMode].hasOwnProperty("errors"))
        return null;

      return priceData.options[productionMode][price_key];
    }

    return null; // production mode absent from this price data
  },

  getUnitPrice: function (
    priceData,
    productionMode,
    finish,
    painting,
    business_rewards
  ) {
    if (!this.isValidMode(productionMode)) {
      console.error("Invalid production mode '" + productionMode + "'");
      return null;
    }

    var price_key = "unit_price";
    if (business_rewards) {
      price_key = "unit_price_no_business_rewards";
    }
    if (productionMode === PRODUCTION_MODE.STANDARD) {
      if (!priceData || priceData.error) return null;

      return (
        priceData.price[price_key] +
        PriceDataUtils._getFinishAndPaintingPrice(priceData, finish, painting)
      );
    } else if (
      priceData.hasOwnProperty("options") &&
      priceData.options.hasOwnProperty(productionMode)
    ) {
      if (priceData.options[productionMode].hasOwnProperty("errors"))
        return null;

      return priceData.options[productionMode][price_key];
    }

    return null; // production mode absent from this price data
  },

  getUnitPriceNoDiscount: function (
    priceData,
    productionMode,
    finish,
    painting,
    business_rewards
  ) {
    if (!this.isValidMode(productionMode)) {
      console.error("Invalid production mode '" + productionMode + "'");
      return null;
    }

    var price_key = "unit_price_nodiscount";
    if (business_rewards) {
      price_key = "unit_price_no_business_rewards";
    }
    if (productionMode === PRODUCTION_MODE.STANDARD) {
      if (priceData.error) return null;

      if (!priceData.price.hasOwnProperty(price_key)) {
        // it seems that standard_mode doesn't always provide a nodiscount price
        // return unit_price so that it feels normal downstream
        return priceData.price.unit_price;
      }

      return (
        priceData.price[price_key] +
        PriceDataUtils._getFinishAndPaintingPrice(priceData, finish, painting)
      ); // NOTE: finish and painting deltas are not discounted themselves
    } else if (
      priceData.hasOwnProperty("options") &&
      priceData.options.hasOwnProperty(productionMode)
    ) {
      if (priceData.options[productionMode].hasOwnProperty("errors"))
        return null;

      return priceData.options[productionMode][price_key];
    }

    return null; // production mode absent from this price data
  },

  /**
   * Get price of finish and/or painting options
   * This is relevant to standard_mode only. In all but standard_mode the pricing information already accounts for finish and painting price
   */
  _getFinishAndPaintingPrice: function (priceData, finish, painting) {
    var price = 0;

    if (!priceData.hasOwnProperty("options")) {
      console.warn("No options in this priceData", priceData);
      return 0;
    }

    if (
      painting &&
      priceData.options.hasOwnProperty("painting") &&
      priceData.options.painting.hasOwnProperty(painting)
    ) {
      price += priceData.options.painting[painting].price_delta;
    }

    if (
      finish &&
      priceData.options.hasOwnProperty("finish") &&
      priceData.options.finish.hasOwnProperty(finish)
    ) {
      price += priceData.options.finish[finish].price_delta;
    }

    return price;
  },

  getErrors: function (priceData, productionMode, get_error_id) {
    if (!this.isValidMode(productionMode)) {
      console.error("Invalid production mode '" + productionMode + "'");
      return [];
    }

    if (productionMode === PRODUCTION_MODE.STANDARD) {
      if (priceData.hasOwnProperty("errors"))
        console.warn("has errors", priceData.errors, priceData);

      if (priceData.error) {
        if (priceData.hasOwnProperty("error_info")) {
          // TODO: check, the 'new print page' introduced the error_info object with more details and
          // the translated msg although it's only for standard_mode (and buggy for min_dim_guidelines,
          // as it has the id instead of the tranlated msg)
          if (priceData.error_info.ID == "min_dim_guidelines") {
            // Patch the min_dim_guidelines case. TODO: this is a quick fix, fix it server side
            return [priceData.error_info.data];
          } else if (priceData.error_info.ID == "tribofinishing_guidelines") {
            // Patch the tribofinishing_guidelines case too. TODO: this is a quick fix, fix it server side
            return ["Not available for this configuration"];
          }
          if (get_error_id) return [priceData.error_info.ID];
          else return [priceData.error_info.text];
        }
        return [priceData.error];
      }
      return [];
    } else if (
      priceData.hasOwnProperty("options") &&
      priceData.options.hasOwnProperty(productionMode)
    ) {
      if (priceData.options[productionMode].hasOwnProperty("errors"))
        return priceData.options[productionMode].errors;

      return [];
    }

    return ["Not available for this configuration"]; // production mode absent from this price data
  },

  getDelivery: function (priceData, productionMode, finish, painting) {
    if (!this.isValidMode(productionMode)) {
      console.error("Invalid production mode '" + productionMode + "'");
      return [];
    }

    if (productionMode == PRODUCTION_MODE.STANDARD) {
      if (priceData.error) return null;

      return PriceDataUtils._getStandardDelivery(priceData, finish, painting);
    } else if (
      priceData.hasOwnProperty("options") &&
      priceData.options.hasOwnProperty(productionMode)
    ) {
      if (priceData.options[productionMode].hasOwnProperty("errors"))
        return null;

      if (
        !priceData.options[productionMode].hasOwnProperty("delivery") ||
        !priceData.options[productionMode].delivery.hasOwnProperty(
          "receipt_date"
        )
      ) {
        console.warn("no delivery info", priceData.options[productionMode]);
        return null;
      }

      return priceData.options[productionMode].delivery.receipt_date;
    }

    console.warn("production mode absent from this price data", priceData);
    return null;
  },

  _getStandardDelivery: function (priceData, finish, painting) {
    // For standard mode, options surely have a further delay than the raw object. We display the maximum
    // found date corresponding to the current options.

    if (
      !priceData.hasOwnProperty("delivery") ||
      !priceData.delivery.hasOwnProperty("receipt_date")
    ) {
      console.warn("no delivery info", priceData);
      return null;
    }

    if (!priceData.hasOwnProperty("options")) {
      console.warn("no options in this priceData", priceData);
      return null;
    }

    var standardDateText = priceData.delivery.receipt_date;
    var standardDate = Date.parse(priceData.delivery.receipt_date_iso);
    var finishDate;

    if (
      finish &&
      priceData.options.hasOwnProperty("finish") &&
      priceData.options.finish.hasOwnProperty(finish) &&
      priceData.options.finish[finish].hasOwnProperty("delivery")
    ) {
      finishDate = Date.parse(
        priceData.options.finish[finish].delivery.receipt_date_iso
      );
      if (finishDate > standardDate || !standardDate) {
        standardDate = finishDate;
        standardDateText =
          priceData.options.finish[finish].delivery.receipt_date;
      }
    }

    if (
      painting &&
      priceData.options.hasOwnProperty("painting") &&
      priceData.options.painting.hasOwnProperty(painting) &&
      priceData.options.painting[painting].hasOwnProperty("delivery")
    ) {
      finishDate = Date.parse(
        priceData.options.painting[painting].delivery.receipt_date_iso
      );
      if (finishDate > standardDate) {
        standardDate = finishDate;
        standardDateText =
          priceData.options.painting[painting].delivery.receipt_date;
      }
    }

    return standardDateText;
  },

  getObject: function (priceData, productionMode, finish, painting) {
    // EXPERIMENTAL
    return {
      unit: PriceDataUtils.getUnitPrice(
        priceData,
        productionMode,
        finish,
        painting
      ),
      total: PriceDataUtils.getTotalPrice(
        priceData,
        productionMode,
        finish,
        painting
      ),
      errors: PriceDataUtils.getErrors(priceData, productionMode),
    };
  },

  getBusinessRewardsPrice: function (
    priceData,
    productionMode,
    finish,
    painting
  ) {
    return this.getTotalPrice(
      priceData,
      productionMode,
      finish,
      painting,
      true
    );
  },

  hasBusinessRewardsPrices: function (quantities, prices) {
    if (
      !prices[1]["error"] &&
      prices[1]["price"].hasOwnProperty("unit_price_no_business_rewards") &&
      prices[1].hasOwnProperty("price") &&
      prices[1]["price"]["unit_price_no_business_rewards"] ===
        prices[1]["price"]["unit_price"]
    ) {
      // if the unit price is the same, there are no business rewards discounts (status < Platinum)
      return false;
    }

    for (var i = 0; i < quantities.length; i++) {
      var quantity = quantities[i];
      if (
        prices.hasOwnProperty(quantity) &&
        prices[quantity].hasOwnProperty("price") &&
        prices[quantity]["price"].hasOwnProperty(
          "unit_price_no_business_rewards"
        )
      ) {
        return true;
      }
    }
    return false;
  },
};
